<template>
    <b-container>
        <b-row v-if="asset_progress && asset_progress.active" class="mb-3">
            <b-col cols="12">
                <div class="border rounded bg-white p-4 text-center">
                    <p class="f-130 mb-2">{{ asset_stage_text }}</p>
                    <b-progress :max="100" height="1.25rem" class="mb-2">
                        <b-progress-bar :value="asset_percent || 0" :label="asset_percent === null ? '' : `${asset_percent}%`" />
                    </b-progress>
                    <small class="text-muted">
                        <template v-if="asset_percent === null">{{ asset_text.measuring }}</template>
                        <template v-else>
                            {{ formatMB(asset_bytes.loaded) }} / {{ formatMB(asset_bytes.total) }} MB
                            · {{ asset_text.remain }} {{ formatMB(asset_bytes.total - asset_bytes.loaded) }} MB
                        </template>
                        · {{ asset_done_count }} / {{ asset_progress.files.length }}
                    </small>
                </div>
            </b-col>
        </b-row>

        <template v-if="view">
            <template v-if="!reportPDFLoaded">
                <b-row>
                    <b-col cols="12">
                        <div class="d-flex align-items-center justify-content-center p-5">
                            
                            <template v-if="ContainerReportLang === view_report_lang">
                                <p v-if="$i18n.locale === 'ko'" class="d-inline-block f-150 mb-0"> 결과보고서 PDF 불러오는 중... </p>
                                <p v-else-if="$i18n.locale === 'en'" class="d-inline-block f-150 mb-0"> Loading on the Report PDF... </p>
                                <p v-else class="d-inline-block f-150 mb-0"> 正在載入 PDF 報告... </p>
                            </template>
                            <template v-else>
                                <p v-if="$i18n.locale === 'ko'" class="d-inline-block f-150 mb-0"> 결과보고서 번역 및 PDF 불러오는 중... </p>
                                <p v-else-if="$i18n.locale === 'en'" class="d-inline-block f-150 mb-0"> Translating the report and loading the PDF... </p>
                                <p v-else class="d-inline-block f-150 mb-0"> 正在翻译报告并加载 PDF...</p>
                            </template>

                            <b-spinner class="ml-2" style="width: 1.5rem;height: 1.5rem;" />
                        </div>
                    </b-col>
                </b-row>
            </template>
            <template v-else>
                <b-row class="mb-4">
                    <b-col cols="12">
                        <b-card no-body :header="$t('결과보고서 정보')" header-class="h5" body-class="overflow-auto vh-100">
                            <b-table-simple>
                                <b-thead>
                                    <b-th>{{ $t('프로젝트 코드') }}</b-th>
                                    <b-th>{{ $t('기업명') }}</b-th>
                                    <b-th>{{ $t('결과보고서 생성일') }}</b-th>
                                    <b-th>{{ $t('결과보고서명') }}</b-th>
                                </b-thead>
                                <b-tbody>
                                    <b-td style="width: 15%;">
                                        {{ view_target_report.project_code }}
                                    </b-td>
                                    <b-td style="width: 20%;">
                                        {{ view_target_report.company_name }}
                                    </b-td>
                                    <b-td style="width: 15%;">
                                        {{ formatDate(view_target_report?.report?.time_stamp) }}
                                    </b-td>
                                    <b-td>
                                        <span class="text-break-all">
                                            {{ ContainerPDFFilename }}
                                        </span>
                                        (<b-link @click.prevent="downloadReport">Download</b-link>)
                                    </b-td>
                                </b-tbody>
                            </b-table-simple>
                        </b-card>
                    </b-col>
                </b-row>
                <b-row align-h="center">
                    <b-col cols="12">
                        <b-card body-class="bgray-200 overflow-auto vh-100">
                            <template #header>
                                <div class="d-flex justify-content-between align-items-center">
                                    <h6 class="mb-0">{{$t('결과보고서 뷰어')}}</h6>

                                    <b-btn-group v-if="show_lang_selector">
                                        <b-btn :variant="ContainerReportLang === 'ko' ? 'danger' : 'secondary'" @click="ContainerReportLang = 'ko'">KOR</b-btn>
                                        <b-btn :variant="ContainerReportLang === 'en' ? 'danger' : 'secondary'" @click="ContainerReportLang = 'en'">ENG</b-btn>
                                        <b-btn :variant="ContainerReportLang === 'ca' ? 'danger' : 'secondary'" @click="ContainerReportLang = 'ca'">CHI</b-btn>
                                    </b-btn-group>
                                </div>
                            </template>

                            <b-row class="justify-content-center">
                                <b-col cols="12" xl="10" xxl="10" class="">
                                    <template v-if="report_preview_url">
                                        <vue-pdf-embed ref="report-viewer"
                                            :source="report_preview_url"
                                            :textLayer="true"
                                            :use-only-css-zoom="true"
                                            @loaded="onReportLoaded"
                                            />
                                    </template>
                                </b-col>
                            </b-row>
                        </b-card>
                    </b-col>
                </b-row>
            </template>
        </template>

        <template v-if="preview || (view && ContainerReportLang !== view_report_lang && is_need_report_blob)">
            <component :is="active_report_viewer"
                       v-if="active_report_viewer"
                       ref="resultReport"
                       :class="[view ? 'd-none' : null]"
                       :report_lang="ContainerReportLang"
                       :needReport="is_need_report_blob"
                       :reportMode="reportMode"
                       :assessmentType="viewer_assessment_type"
                       :pdf_filename="pdf_filename"
                       :reportData="ContainerReportData"
                       :is_reassessment="is_reassessment"
                       :set_actual_duration="set_actual_duration"
                       @sendReportBlob="sendReportBlob"
                       @self-report-update="(data) => $emit('self-report-update', data)"
            />
        </template>
    </b-container>
</template>

<script>
import VuePdfEmbed from 'vue-pdf-embed/dist/vue2-pdf-embed';
import ReportNamingMixin from '@shared_mixins/report/report.naming.mixin.js';
import {REPORT_RENDERERS} from '@shared_mixins/report/report-template';
import ReportViewerHMC from '@shared_components/admin/components/report/ReportViewerHMC.vue';
import ReportViewerKIA from '@shared_components/admin/components/report/ReportViewerKIA.vue';
import ReportViewerHYUNDAI from '@shared/components/admin/components/report/ReportViewerHYUNDAI.vue';
import ReportViewerHMC_2025 from '@shared/components/admin/components/report/ReportViewerHMC_2025.vue';
import {mapActions} from 'vuex';
import ReportViewerKSTEEL from '@shared/components/admin/components/report/ReportViewerKSTEEL.vue';
import ReportViewerSUPPLY from '@shared/components/admin/components/report/ReportViewerSUPPLY.vue';
import ReportViewerHYUNDAI_V2 from '@shared/components/admin/components/report/ReportViewerHYUNDAI_V2.vue';
import ReportViewerHMC_THEME from '@shared/components/admin/components/report/ReportViewerHMC_THEME.vue';

export default {
    name: 'ReportViewerContainer',
    mixins: [
        ReportNamingMixin
    ],
    components: {
        ReportViewerKSTEEL,
        ReportViewerSUPPLY,
        ReportViewerHYUNDAI_V2,
        ReportViewerHMC_THEME,
        ReportViewerHMC_2025,
        ReportViewerHYUNDAI,
        VuePdfEmbed,
        ReportViewerHMC,
        ReportViewerKIA
    },
    props: {
        report_lang: {
            required: false,
            type: String,
            default: 'ko'
        },
        // 원청사 대시보드, 기업용 결과보고서 보기용
        view: {
            required: false,
            type: Boolean,
            default: false
        },
        view_target_report: {
            required: false,
            type: Object
        },

        // 관리자 평가결과 페이지 - 결과보고서 미리보기용
        preview: {
            required: false,
            type: Boolean,
            default: false
        },
        reportData: {
            required: false,
            type: Object,
            default: () => ({ info: null, benchmarks: null, parser: null, risk: null, scoring: null })
        },
        pdf_filename: {
            required: false,
            type: String,
            default: ''
        },
        reportMode: {
            required: false,
            type: String,
            default: 'full'
        },
        needReport: {
            required: false,
            type: Boolean,
            default: false
        },
        assessmentType: {
            required: false,
            type: String,
            default: 'DOC'
        },
        is_reassessment: {
            required: false,
            type: Boolean,
            default: false
        },
        set_actual_duration: {
            required: false,
            type: Boolean,
            default: true
        }
    },
    data() {
        return {
            is_need_report_blob: false,
            reportPDFLoaded: false,
            report_preview_url: null,
            ContainerPDFFilename: null,
            ContainerReportLang: 'ko',
            ContainerReportData: { info: null, benchmarks: null, parser: null, risk: null, scoring: null }
        };
    },
    computed: {
        // 보고서 에셋(폰트·이미지) 준비 상태 — report.font.plugin.js
        asset_progress () {
            return this.$report && this.$report.assetProgress;
        },
        asset_done_count () {
            return this.asset_progress.files.filter(_file => _file.done).length;
        },
        asset_bytes () {
            return this.asset_progress.files.reduce((_acc, _file) => ({
                loaded: _acc.loaded + _file.loaded,
                total: _acc.total + (_file.total || 0)
            }), { loaded: 0, total: 0 });
        },
        // 모든 파일의 크기를 알기 전에는 퍼센트를 내지 않는다 (부정확한 값 표시 방지)
        asset_percent () {
            const { loaded, total } = this.asset_bytes;
            if (!total || this.asset_progress.files.some(_file => _file.total === null)) return null;
            return Math.floor(loaded / total * 100);
        },
        asset_text () {
            const _texts = {
                ko: { opening: '보고서의 문을 여는 중...', images: '사진을 가져오는 중...', fonts: '글꼴을 챙기는 중...', measuring: '짐 크기를 재는 중', remain: '남은 짐' },
                en: { opening: 'Opening the gate...', images: 'Fetching the pictures...', fonts: 'Packing the fonts...', measuring: 'Measuring the load', remain: 'Remaining' },
                ca: { opening: '正在打开大门...', images: '正在获取图片...', fonts: '正在准备字体...', measuring: '正在计算大小', remain: '剩余' },
            };
            return _texts[this.$i18n.locale] || _texts.ko;
        },
        asset_stage_text () {
            if (this.asset_percent === null) return this.asset_text.opening;
            const _images_pending = this.asset_progress.files.some(_file => _file.kind === 'image' && !_file.done);
            return _images_pending ? this.asset_text.images : this.asset_text.fonts;
        },
        // 판정(resolved_report_template, naming mixin)에 따른 뷰어 컴포넌트 등록명.
        // null 이면 아무 뷰어도 마운트하지 않는다 (종전 v-else-if 체인 fall-through 와 동일).
        active_report_viewer () {
            const template = this.resolved_report_template;
            return template ? REPORT_RENDERERS[template].viewer : null;
        },
        // 종전 체인에서 hmc/hmc_2025/hyundai/kia 는 selected_assessment_type, ksteel/supply 는 assessmentType prop 을 넘겼다.
        viewer_assessment_type () {
            const template = this.resolved_report_template;
            return template && REPORT_RENDERERS[template].assessmentTypeSource === 'prop' ? this.assessmentType : this.selected_assessment_type;
        },
        selected_assessment_type () {
            if (this.view && this.view_target_report.report_type) {
                return this.view_target_report.report_type.toLocaleUpperCase();
            } else if (this.preview && this.reportData.info.service_type) {
                return this.reportData.info.service_type === '일반' ? 'DOC' : 'OSIS';
            }

            return null;
        },
        project_code () {
            if (this.view && this.view_target_report.project_code) {
                return this.view_target_report.project_code;
            } else if (this.preview && this.reportData?.info?.service_type) {
                return this.reportData.info.company_id;
            }
            return null;
        },
        company_id () {
            if (this.view && this.view_target_report.company_id) {
                return this.view_target_report.company_id;
            } else if (this.preview && this.reportData.info.company_id) {
                return this.reportData.info.company_id;
            }
            return null;
        },
        company_name () {
            if (this.view && this.view_target_report.company_name) {
                return this.view_target_report.company_name;
            } else if (this.preview && this.reportData.info.company_name) {
                return this.reportData.info.company_name;
            }
            return null;
        },
        vendor_code () {
            if (this.view && this.view_target_report.vendor_code) {
                return this.view_target_report.vendor_code;
            } else if (this.preview && this.reportData.info.vendor_code) {
                return this.reportData.info.vendor_code;
            }
            return null;
        },
        show_lang_selector () {
            if (this.view && this.view_target_report?.report) {
                return this.view_target_report.report.has_multi_lang !== false;
            }
            return true;
        },
        view_report_lang () {
            if (this.view && this.view_target_report.company_id) {
                return this.view_target_report.report.report_lang;
            } else if (this.preview && this.reportData[`report_${this.selected_assessment_type.toLocaleLowerCase()}`]) {
                return this.reportData[`report_${this.selected_assessment_type.toLocaleLowerCase()}`].report_lang;
            }
            return this.ContainerReportLang;
        }
    },
    watch: {
        report_lang (_lang) {
            this.ContainerReportLang = _lang;
        },
        async ContainerReportLang (_container_report_lang) {
            if (this.view) {
                await this.loadReport();
            }
        },
        needReport (_need_report) {
            if (_need_report) {
                this.is_need_report_blob = _need_report;
            }
        }
    },
    beforeCreate () {
        if (this.report_preview_url && this.report_preview_url.includes('blob')) {
            URL.revokeObjectURL(this.report_preview_url);
            this.report_preview_url = null;
        }

        this.reportPDFLoaded = false;
    },
    async created() {
        if (this.pdf_filename) {
            this.ContainerPDFFilename = this.pdf_filename;
        } else {
            this.ContainerPDFFilename = this.report_filename;
        }

        this.ContainerReportLang = this.report_lang;
        if (this.preview) {
            this.ContainerReportData = this.reportData;
        } else if (this.view) {
            await this.loadReport();
        }

        await this.fetchAuditCategory();
    },
    mounted() {},
    methods: {
        formatMB (_bytes) {
            return (_bytes / 1024 / 1024).toFixed(1);
        },
        ...mapActions('evaluations', ['fetchAuditCategory']),
        async loadReport () {
            try {
                this.reportPDFLoaded = false;

                if (this.report_preview_url && this.report_preview_url.includes('blob')) {
                    URL.revokeObjectURL(this.report_preview_url);
                    this.report_preview_url = null;
                }

                if (this.ContainerReportLang !== this.view_report_lang) {
                    const _res_report_score_set = await this.$axios.post('/report/download', {
                        project_code: this.project_code,
                        company_id_list: [this.company_id],
                        mode: this.selected_assessment_type,
                        type: 'json'
                    }, {
                        responseType: 'json'
                    }
                    );
                    
                    const _reportData = _res_report_score_set.data;
                    if (_reportData.parser_result) {
                        _reportData.parser = _reportData.parser_result;
                        delete _reportData['parser_result'];
                    }

                    this.ContainerReportData = _reportData;
                    this.$nextTick(() => {
                        this.is_need_report_blob = true;
                    });
                } else {
                    this.ContainerPDFFilename = this.view_target_report.report.report_name;
                    const _res_report_pdf = await this.$axios.post('/report/download', {
                        project_code: this.project_code,
                        company_id_list: [this.company_id],
                        mode: this.selected_assessment_type,
                        type: 'pdf'
                    }, {
                        responseType: 'blob'
                    }
                    );
                    
                    const _report_pdf_blob = new Blob([_res_report_pdf.data], { type: 'application/pdf' });
                    this.report_preview_url = URL.createObjectURL(_report_pdf_blob);
                    this.reportPDFLoaded = true;
                }
            } catch (error) {
                alert('죄송합니다. 결과보고서 처리 도중 장애가 발생했습니다. QESG 개발팀에 문의해주세요');
            } finally {
                // this.reportPDFLoaded = true
            }
        },
        async downloadReport () {
            try {
                const link = document.createElement('a');
                link.href = this.report_preview_url;
                link.setAttribute('download', this.ContainerPDFFilename);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } catch (error) {
                alert(this.$t('죄송합니다. 결과보고서를 불러오는 도중 장애가 발생했습니다. QESG info@qesg.co.kr 로 문의해주시기 바랍니다.'));
            }
        },
        onReportLoaded () {
            this.reportPDFLoaded = true;
        },
        sendReportBlob (reportBlob) {
            if (this.view) {
                const _report_pdf_blob = new Blob([reportBlob.data], { type: 'application/pdf' });
                this.report_preview_url = URL.createObjectURL(_report_pdf_blob);
                this.reportPDFLoaded = true;
                this.is_need_report_blob = false;

                this.ContainerPDFFilename = reportBlob.filename;
            } else if (this.preview) {
                this.$emit('sendReportBlob', reportBlob.data);
            }
        }
    }
};
</script>

<style lang="scss">
.vue-pdf-embed > div {
  margin-bottom: 8px;
  box-shadow: 0 2px 8px 4px rgba(0, 0, 0, 0.1);
}
</style>
